import { UnleashClient } from 'unleash-proxy-client';

import activeAuthServiceRegistry from '@rygen/rygen-ui/service/ActiveAuthServiceRegistry';
import {
  getLastAuthProvider,
  type AuthProvider,
} from '@rygen/rygen-ui/service/AuthProviderBreadcrumb';
import { resolveActiveProvider } from '@rygen/rygen-ui/service/AuthProviderResolver';
import authService from '@rygen/rygen-ui/service/AuthService';
import keycloakAuthService from '@rygen/rygen-ui/service/KeycloakAuthService';
import { resolveLoginOverride } from '@rygen/rygen-ui/service/KeycloakLoginOverride';

// Two-gate check for Keycloak:
//   1. KEYCLOAK_AUTH_ENABLED env property
//   2. keycloak-auth-feat-flag-kill-switch Unleash flag (runtime killswitch)
// Both must be true. Either being false, or Unleash being unreachable, forces Azure.
// ?login= overrides: 'keycloak' (beta opt-in) bypasses only the env property and
// still requires the killswitch; 'keycloak-debug'/'azure-debug' bypass both gates.
const KEYCLOAK_FLAG = 'keycloak-auth-feat-flag-kill-switch';
const FLAG_FETCH_TIMEOUT_MS = 3_000;

let activeAuthProvider: AuthProvider | null = null;

export function getActiveAuthProvider(): AuthProvider | null {
  return activeAuthProvider;
}

// The killswitch must be explicitly enabled; a missing flag or unreachable
// Unleash counts as off, failing safe to Azure.
async function resolveKillswitchEnabled(
  properties: Record<string, string>,
): Promise<boolean> {
  try {
    const client = new UnleashClient({
      url: properties['UNLEASH_URL'],
      clientKey: properties['UNLEASH_CLIENT_KEY'],
      appName: properties['UNLEASH_ENVIRONMENT_NAME'],
      refreshInterval: 0,
      disableMetrics: true,
      context: {
        properties: {
          environmentName: properties['ENVIRONMENT_NAME'],
        },
      },
    });

    await Promise.race([
      client.start().catch(() => {}),
      new Promise<void>((resolve) =>
        setTimeout(resolve, FLAG_FETCH_TIMEOUT_MS),
      ),
    ]);

    try {
      return client.isEnabled(KEYCLOAK_FLAG);
    } finally {
      client.stop();
    }
  } catch (e) {
    console.warn('[auth] Unleash unreachable, treating killswitch as off', e);
    return false;
  }
}

export async function installAuthProvider(
  properties: Record<string, string>,
): Promise<void> {
  const override = resolveLoginOverride();
  const envEnabled = properties['KEYCLOAK_AUTH_ENABLED'] === 'true';

  // Only consult Unleash when the outcome can depend on it. The beta opt-in
  // needs the killswitch state even in env-disabled environments; the -debug
  // overrides and plain env-disabled loads never do.
  let killswitchEnabled = false;
  if (envEnabled || override === 'keycloak') {
    try {
      killswitchEnabled = await resolveKillswitchEnabled(properties);
    } catch (e) {
      console.error(
        '[auth] Failed to resolve killswitch, falling back to Azure',
        e,
      );
    }
  }

  // ?login= overrides: 'keycloak' is the beta opt-in and requires the
  // killswitch to be on; the -debug values are deliberate escape hatches
  // for testing and bypass both gates.
  let useKeycloak: boolean;
  switch (override) {
    case 'keycloak-debug':
      useKeycloak = true;
      break;
    case 'azure-debug':
      useKeycloak = false;
      break;
    case 'keycloak':
      useKeycloak = killswitchEnabled;
      break;
    default:
      useKeycloak = envEnabled && killswitchEnabled;
  }
  const isExplicitOverride =
    override != null && !(override === 'keycloak' && !killswitchEnabled);

  // The gate decides the default for new logins, but a returning user must
  // re-authenticate against the provider that issued their last token (the
  // breadcrumb) — otherwise an expired Azure token would bounce them to Keycloak
  // (or vice versa). resolveActiveProvider reconciles the two.
  const gate = useKeycloak ? 'keycloak' : 'azure';
  const azureConfigured = !!properties['AZURE_CLIENT_ID'];
  const keycloakConfigured = !!properties['KEYCLOAK_URL'];
  const lastProvider = getLastAuthProvider();

  // Transitional, and only when Keycloak is the gate: a returning Azure user who
  // predates the breadcrumb has no marker yet, so probe MSAL for a cached account
  // to keep them on Azure. When the gate is Azure (incl. killswitch off) the
  // resolver forces Azure regardless, so the probe is unnecessary.
  let hasExistingAzureSession = false;
  if (gate === 'keycloak' && !lastProvider && azureConfigured) {
    try {
      authService.init(properties);
      hasExistingAzureSession = await authService
        .getInstance()
        .hasCachedAccount();
    } catch (e) {
      console.warn('[auth] Azure session probe failed, ignoring', e);
    }
  }

  const provider = resolveActiveProvider({
    gate,
    lastProvider,
    azureConfigured,
    keycloakConfigured,
    hasExistingAzureSession,
    isExplicitOverride,
  });

  const activeService =
    provider === 'keycloak' ? keycloakAuthService : authService;
  try {
    activeService.init(properties);
    activeAuthServiceRegistry.register(activeService.getInstance());
    activeAuthProvider = provider;
  } catch (e) {
    console.error('[auth] Critical: failed to initialize auth provider', e);
  }
}
